import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { logAuditEvent } from '@/lib/logging/auditLog';
import { captureAppError } from '@/lib/monitoring/sentry';
import { logSystemEvent, generateRequestId } from '@/lib/observability/logSystemEvent';
import { buildSystemEventContext } from '@/lib/observability/buildSystemEventContext';
import { track } from '@/lib/analytics';
import * as Sentry from '@sentry/react';

/** Structured error returned by publish_producer RPC */
interface PublishResult {
  success: boolean;
  error_key?: string;
  reason_kind?: string;
  message_sv?: string;
}

/** Map error_key to Swedish toast */
const GUARD_TOASTS: Record<string, { title: string; description: string }> = {
  not_member: {
    title: 'Behörighet saknas',
    description: 'Du saknar behörighet att publicera denna profil.',
  },
  producer_not_found: {
    title: 'Profilen hittades inte',
    description: 'Något gick fel. Ladda om sidan och försök igen.',
  },
  producer_suspended: {
    title: 'Profilen är pausad',
    description: 'Din värdprofil kan inte publiceras just nu. Kontakta Farmly-teamet för hjälp.',
  },
  missing_required_fields: {
    title: 'Fyll i namn och beskrivning',
    description: 'För att publicera behöver du ange namn och en kort beskrivning.',
  },
  place_not_linked: {
    title: 'Ingen plats kopplad',
    description: 'Din värdprofil behöver vara kopplad till en plats innan du kan publicera.',
  },
  place_not_published: {
    title: 'Platsen är inte publicerad ännu',
    description: 'Platsen din profil är kopplad till behöver publiceras först. Kontakta Farmly-teamet.',
  },
};

const SYSTEM_TOAST = {
  title: 'Något gick fel',
  description: 'Kunde inte publicera just nu. Försök igen.',
  variant: 'destructive' as const,
};

/**
 * Publish a producer via the enforced RPC.
 * RPC now returns structured JSON { success, error_key, reason_kind, message_sv }.
 */
export function usePublishProducer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (producerId: string) => {
      const { data, error } = await supabase.rpc('publish_producer', {
        p_producer_id: producerId,
      });

      // Network / Supabase-level error (e.g. 500, auth failure)
      if (error) throw error;

      // RPC returned structured guard/validation error
      const result = (typeof data === 'string' ? JSON.parse(data) : data) as PublishResult;
      if (!result.success) {
        const guardError = new Error(result.message_sv || 'Publish failed');
        (guardError as any).error_key = result.error_key;
        (guardError as any).reason_kind = result.reason_kind;
        (guardError as any).is_structured = true;
        throw guardError;
      }

      return result;
    },
    onSuccess: async (_, producerId) => {
      const requestId = generateRequestId();

      queryClient.invalidateQueries({ queryKey: ['producer-capabilities', producerId] });
      queryClient.invalidateQueries({ queryKey: ['producer', producerId] });
      queryClient.invalidateQueries({ queryKey: ['my-producers'] });

      await logAuditEvent({
        entityType: 'producer',
        entityId: producerId,
        action: 'published',
        message: 'Producent publicerad via RPC',
      });

      logSystemEvent({
        level: 'info',
        event_key: 'producer_published',
        domain: 'producer',
        entity_type: 'producer',
        entity_id: producerId,
        producer_id: producerId,
        message: 'Producer published successfully',
        request_id: requestId,
        context: buildSystemEventContext({ feature: 'publish_producer', action: 'publish', surface: 'onboarding' }),
      });

      track('producer_publish_succeeded', {
        surface: 'onboarding',
      });

      toast({
        title: 'Publicerad!',
        description: 'Din gård är nu synlig för besökare på Farmly.',
      });
    },
    onError: (err: any, producerId) => {
      const requestId = generateRequestId();
      const isStructured = err?.is_structured === true;
      const errorKey: string = err?.error_key ?? 'system_error';
      const reasonKind: string = err?.reason_kind ?? 'server';

      // Dev logging
      if (process.env.NODE_ENV === 'development') {
        console.debug('[usePublishProducer] error', {
          request_id: requestId,
          reason_kind: reasonKind,
          error_key: errorKey,
          message: err?.message,
          code: err?.code,
          is_structured: isStructured,
        });
      }

      // system_events (fire-and-forget)
      const eventKey = isStructured
        ? `producer_publish_failed_${reasonKind}`
        : 'producer_publish_failed_system';

      logSystemEvent({
        level: isStructured && reasonKind !== 'server' ? 'warn' : 'error',
        event_key: eventKey,
        domain: 'producer',
        entity_type: 'producer',
        entity_id: producerId,
        producer_id: producerId,
        message: `Publish failed: ${errorKey}`,
        request_id: requestId,
        context: buildSystemEventContext({
          feature: 'publish_producer',
          action: 'publish',
          reason_kind: reasonKind as any,
          surface: 'onboarding',
          error_code: errorKey,
          supabase_code: err?.code,
        }),
      });

      // Sentry
      if (!isStructured || reasonKind === 'server') {
        captureAppError(err, {
          feature: 'publish_producer',
          action: 'publish_failed',
          producer_id: producerId,
          error_code: err?.code,
          request_id: requestId,
          reason_kind: reasonKind,
          error_key: errorKey,
        });
      } else {
        Sentry.captureMessage(`Publish blocked: ${errorKey}`, {
          level: 'warning',
          tags: {
            feature: 'publish_producer',
            reason_kind: reasonKind,
            error_key: errorKey,
          },
          extra: {
            producer_id: producerId,
            request_id: requestId,
          },
        });
      }

      // PostHog
      track('publish_producer_failed', {
        error_code: errorKey,
        is_validation: reasonKind === 'validation' || reasonKind === 'guard',
      });

      // Toast — structured errors get calm guidance, system errors get red
      if (isStructured && GUARD_TOASTS[errorKey]) {
        toast(GUARD_TOASTS[errorKey]);
      } else if (isStructured) {
        // Unknown structured error — still calm
        toast({
          title: 'Kan inte publicera',
          description: err?.message || 'Kontrollera att all information är ifylld och försök igen.',
        });
      } else {
        toast(SYSTEM_TOAST);
      }
    },
  });
}

/**
 * Unpublish a producer via the enforced RPC.
 */
export function useUnpublishProducer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (producerId: string) => {
      const { error } = await supabase.rpc('unpublish_producer', {
        p_producer_id: producerId,
      });
      if (error) throw error;
    },
    onSuccess: async (_, producerId) => {
      queryClient.invalidateQueries({ queryKey: ['producer-capabilities', producerId] });
      queryClient.invalidateQueries({ queryKey: ['producer', producerId] });
      queryClient.invalidateQueries({ queryKey: ['my-producers'] });

      await logAuditEvent({
        entityType: 'producer',
        entityId: producerId,
        action: 'unpublished',
        message: 'Producent avpublicerad via RPC',
      });

      toast({
        title: 'Avpublicerad',
        description: 'Din gård är nu dold för besökare.',
      });
    },
    onError: () => {
      toast({
        title: 'Något gick fel',
        description: 'Kunde inte avpublicera just nu. Försök igen.',
        variant: 'destructive',
      });
    },
  });
}
